/**
 * A weekly room's Sessions tab: its schedule, the sessions coming up (a topic each, move or skip one), the past
 * ones with their reports, and who keeps coming across the series.
 */
import { useState } from "react";
import { useNavigate } from "react-router";
import type { LiveChangeResponse, RoomAudienceResponse, WebinarDetail, WebinarLive } from "../../../shared/types";
import { daysUntil, labelParts } from "../../../shared/live-time";
import { Button } from "../../components/ui/Button";
import { FaceStack } from "../../components/ui/Face";
import { Icon } from "../../components/ui/Icon";
import { Menu } from "../../components/ui/Menu";
import { longDay, sessionReportPath } from "./liveWords";
import { GROUP_WORDS, sentLine } from "./roomWords";
import { acrossGroup, PAST_SHOWN, SessionDialogs, useSessions } from "./sessionsParts";
import { timeOf } from "../../board/series";

const n = (v: number) => v.toLocaleString("en-US");

function DateBox({ iso, tz, next = false }: { iso: string; tz: string; next?: boolean }) {
  const p = labelParts(iso, tz, "short");
  return (
    <div className={next ? "datebox next" : "datebox"}>
      <span>{p?.month ?? ""}</span>
      <b>{p?.day ?? ""}</b>
    </div>
  );
}

export function SessionsTab({
  detail,
  live,
  across,
  onSent,
  hostFirst,
  onSaved,
}: {
  detail: WebinarDetail;
  live: WebinarLive;
  /** The series' groups (regulars, dropped off, new since the first), when they have loaded. */
  across: RoomAudienceResponse | null;
  /** A note went out: the series' groups are read again, so they say what was sent. */
  onSent: () => void;
  hostFirst: string;
  onSaved: (res: LiveChangeResponse, said: string) => void;
}) {
  const navigate = useNavigate();
  const [allPast, setAllPast] = useState(false);
  const state = useSessions(detail, live, onSaved);
  const { series, tz, now, upcoming, past, ran, first, firstRan, lengthMin, menuFor, setPop, sendDropped } = state;
  if (!series) return null;
  const shownPast = allPast ? past : past.slice(0, PAST_SHOWN);
  const faces = detail.recent_registrants.slice(0, 6).map((r) => ({ name: r.name }));
  const dropped = acrossGroup(across, "dropped");

  return (
    <>
      <section className="card">
        <div className="card-h">
          <div>
            <h2>Schedule</h2>
            <div className="sub">
              {series.long}, {lengthMin} min · {n(ran)} so far{series.ended ? " · ended" : ""}
            </div>
          </div>
          <div className="row g6">
            {series.ended ? null : (
              <>
                <Button size="s" icon="cal" onClick={() => setPop({ kind: "change" })}>
                  Change the time
                </Button>
                <Menu items={[{ label: "End the series", icon: "x", danger: true, onSelect: () => setPop({ kind: "end" }) }]} label="More for the schedule" />
              </>
            )}
          </div>
        </div>
        <div style={{ height: 18 }} />
      </section>

      {upcoming.length ? (
        <section className="card mt20">
          <div className="card-h">
            <h2>Coming up</h2>
          </div>
          <div className="card-b" style={{ padding: "8px 0 4px" }}>
            {upcoming.map((s) => {
              const skipped = s.state === "skipped";
              const next = s === first;
              const days = daysUntil(s.startsAt, s.timezone, now);
              const soon = next ? (s.state === "live" ? "Live now · " : days === 0 ? "Today · " : days === 1 ? "Tomorrow · " : "") : "";
              const items = menuFor(s);
              return (
                <div key={s.id} className={skipped ? "sess skipped" : "sess"}>
                  <DateBox iso={s.startsAt} tz={s.timezone} next={next} />
                  <div>
                    <div className="b7">
                      {soon ? <span className="sess-soon">{soon}</span> : null}
                      {longDay(s.startsAt, s.timezone)}, {timeOf(s.startsAt, s.timezone)}
                    </div>
                    {skipped ? (
                      <div className="small muted">Skipped. Everyone with a seat was told.</div>
                    ) : (
                      <div className="small muted">
                        Topic: {s.topic ? <b className="ink2" dir="auto">{s.topic}</b> : <span className="muted">No topic yet</span>}
                        {s.movedFrom ? ` · moved from ${longDay(s.movedFrom, tz)}` : ""}
                      </div>
                    )}
                    {skipped ? null : (
                      <div className="aud mt8">
                        {faces.length ? <FaceStack people={faces} size={24} more={Math.max(0, s.registered - faces.length)} /> : null}
                        <span className="small">
                          <b>{n(s.registered)} registered</b> so far
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="row g6">
                    {skipped ? null : (
                      <Button size="s" icon="pencil" onClick={() => setPop({ kind: "topic", session: s })}>
                        {s.topic ? "Change its topic" : "Set its topic"}
                      </Button>
                    )}
                    {items.length ? <Menu items={items} label={`More for ${longDay(s.startsAt, s.timezone)}`} /> : null}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      ) : null}

      <section className="card mt20">
        <div className="card-h">
          <h2>Past sessions</h2>
        </div>
        <div className="card-b" style={{ padding: "8px 0 4px" }}>
          {past.length === 0 ? <p className="small muted" style={{ padding: "8px 20px 12px" }}>After each session it shows here, with its report.</p> : null}
          {shownPast.map((s, i) => {
            const pct = s.registered ? Math.round((s.cameLive / s.registered) * 100) : 0;
            const ranIt = s.state === "ended";
            return (
              <div key={s.id} className="sess">
                <DateBox iso={s.startsAt} tz={s.timezone} />
                <div>
                  <div className="b7">{longDay(s.startsAt, s.timezone)}</div>
                  <div className="small muted">
                    {ranIt ? `${n(s.cameLive)} of ${n(s.registered)} came · ${pct}%` : s.state === "missed" ? "Didn't happen" : "Skipped"}
                  </div>
                  {ranIt ? (
                    <div className="bar mt8" style={{ maxWidth: 220 }}>
                      <i style={{ width: `${pct}%` }} />
                    </div>
                  ) : null}
                </div>
                {ranIt ? (
                  <Button
                    size="s"
                    variant={i === 0 ? "soft" : "plain"}
                    after={i === 0 ? "chev" : undefined}
                    onClick={() => navigate(i === 0 ? `/rooms/${detail.id}/live` : sessionReportPath(detail.id, s.id))}
                  >
                    See the report
                  </Button>
                ) : (
                  <span />
                )}
              </div>
            );
          })}
          {past.length > PAST_SHOWN && !allPast ? (
            <div className="small muted" style={{ padding: "8px 20px 12px" }}>
              And {n(past.length - PAST_SHOWN)} earlier {past.length - PAST_SHOWN === 1 ? "session" : "sessions"}
              {firstRan ? `, from ${longDay(firstRan.startsAt, firstRan.timezone)}` : ""}.{" "}
              <button type="button" className="link" onClick={() => setAllPast(true)}>
                Show them
              </button>
            </div>
          ) : null}
        </div>
      </section>

      {ran > 0 ? (
        <section className="card mt20">
          <div className="card-h">
            <div>
              <h2>Across the series</h2>
              {firstRan ? <div className="sub">Since {longDay(firstRan.startsAt, firstRan.timezone).split(" ").slice(1).join(" ")}</div> : null}
            </div>
          </div>
          <div className="card-b grid g3">
            <div className="grp still">
              <div className="row">
                <span className="gn">{n(series.results.regulars)}</span>
                <span className="sp" />
                <FaceStack people={(acrossGroup(across, "regulars")?.faces ?? []).map((f) => ({ name: f.name, photo: f.avatar_url }))} size={26} />
              </div>
              <div className="gl">Regulars</div>
              <div className="gd">Came to every one</div>
            </div>
            <div className="grp still">
              <div className="row">
                <span className="gn">{n(series.results.droppedOff)}</span>
                <span className="sp" />
                <FaceStack people={(dropped?.faces ?? []).map((f) => ({ name: f.name, photo: f.avatar_url }))} size={26} />
              </div>
              <div className="gl">Dropped off</div>
              <div className="gd">Missed the last two</div>
              {dropped?.sent ? (
                <div className="sent">
                  <Icon name="check" />
                  {sentLine(dropped.sent, Date.now())}
                </div>
              ) : null}
              {dropped && dropped.reach > 0 ? (
                <Button
                  size="s"
                  variant="soft"
                  icon="send"
                  onClick={() => sendDropped(dropped, hostFirst)}
                >
                  {dropped.sent ? GROUP_WORDS.dropped.again(dropped.reach) : GROUP_WORDS.dropped.act(dropped.reach)}
                </Button>
              ) : null}
            </div>
            <div className="grp still">
              <div className="row">
                <span className="gn">{n(series.results.joinedLate)}</span>
                <span className="sp" />
                <FaceStack people={(acrossGroup(across, "joined_late")?.faces ?? []).map((f) => ({ name: f.name, photo: f.avatar_url }))} size={26} />
              </div>
              <div className="gl">New since the first</div>
            </div>
          </div>
        </section>
      ) : null}

      <SessionDialogs detail={detail} live={live} state={state} onSaved={onSaved} onSent={onSent} />
    </>
  );
}
